import { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Shield, FileText, AlertTriangle, Scale, Mail } from 'lucide-react';
import { API } from '../config/api';



export default function TermsPage() {
  const [config, setConfig] = useState(null);

  useEffect(() => {
    fetchPlatformConfig();
  }, []);

  const fetchPlatformConfig = async () => {
    try {
      const response = await fetch(`${API}/platform/config`);
      if (response.ok) {
        const data = await response.json();
        setConfig(data);
      }
    } catch (error) {
      console.error('Failed to fetch platform config:', error);
    }
  };

  const contactEmail = config?.legal_contact || 'contact@dmvsoundstage.com';
  const dmcaEmail = config?.dmca_contact || 'dmca@dmvsoundstage.com';

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8" data-testid="terms-page">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-primary/20 text-primary">Legal</Badge>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold uppercase mb-4">
            Terms of <span className="text-secondary">Service</span>
          </h1>
          <p className="text-muted-foreground">Last updated: January 2025</p>
        </div>

        <div className="space-y-8">
          {/* Introduction */}
          <Card className="glass-card border-border p-6">
            <h2 className="font-heading text-xl font-bold uppercase mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Introduction
            </h2>
            <p className="text-muted-foreground mb-4">
              Welcome to DMV Soundstage ("Platform", "we", "us", "our"). DMV Soundstage is a regional music, 
              video, and podcast platform for artists across Washington, DC, Maryland, and Virginia.
            </p>
            <p className="text-muted-foreground">
              By accessing or using our Platform, you agree to be bound by these Terms of Service. 
              If you do not agree to these terms, please do not use our Platform.
            </p>
          </Card>

          {/* User Content Responsibility */}
          <Card className="glass-card border-border p-6">
            <h2 className="font-heading text-xl font-bold uppercase mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-secondary" />
              User Content Responsibility
            </h2>
            <ul className="space-y-3 text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="text-secondary mt-1">•</span>
                All music, videos, podcasts, and media uploaded to DMV Soundstage are uploaded by users.
              </li>
              <li className="flex items-start gap-2">
                <span className="text-secondary mt-1">•</span>
                DMV Soundstage does not own, create, or claim authorship of user-uploaded content.
              </li>
              <li className="flex items-start gap-2">
                <span className="text-secondary mt-1">•</span>
                Uploading users are solely responsible for ensuring they have the legal right to upload and distribute their content.
              </li>
              <li className="flex items-start gap-2">
                <span className="text-secondary mt-1">•</span>
                You represent and warrant that you own or have the necessary rights to all content you upload.
              </li>
            </ul>
          </Card>

          {/* Copyright Representation */}
          <Card className="glass-card border-secondary/30 p-6">
            <h2 className="font-heading text-xl font-bold uppercase mb-4 flex items-center gap-2">
              <Scale className="w-5 h-5 text-secondary" />
              Copyright Representation (Upload Requirement)
            </h2>
            <p className="text-muted-foreground mb-4">
              Before uploading any content, artists must affirm:
            </p>
            <div className="bg-secondary/10 border border-secondary/30 rounded-lg p-4 mb-4">
              <p className="text-foreground font-medium italic">
                "I own this content or have the legal rights and permissions required to upload and 
                distribute it on DMV Soundstage."
              </p>
            </div>
            <p className="text-muted-foreground">
              Uploads cannot proceed unless this affirmation is accepted. By uploading content, 
              you certify that this statement is true.
            </p>
          </Card>

          {/* License to Platform */}
          <Card className="glass-card border-border p-6">
            <h2 className="font-heading text-xl font-bold uppercase mb-4">License to Platform</h2>
            <p className="text-muted-foreground mb-4">
              By uploading content, artists grant DMV Soundstage a <strong>non-exclusive, royalty-free license</strong> to:
            </p>
            <ul className="space-y-2 text-muted-foreground mb-4">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                Host, stream, display, and distribute the content solely for platform functionality and promotion
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                Feature content in charts, playlists, and promotional materials
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                Enable other users to stream and interact with your content
              </li>
            </ul>
            <p className="text-foreground font-medium">
              Artists retain full ownership of their content at all times.
            </p>
          </Card>

          {/* DSP Eligibility */}
          <Card className="glass-card border-border p-6">
            <h2 className="font-heading text-xl font-bold uppercase mb-4">DSP Eligibility Requirement</h2>
            <ul className="space-y-3 text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                DMV Soundstage only permits music that is eligible for distribution on major Digital Service Providers (DSPs).
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                Proper metadata (title, artist name, genre, etc.) is required for all uploads.
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <strong>AI-generated artists are strictly prohibited.</strong> AI tools may assist in production, but the artist must be a real human creator.
              </li>
            </ul>
          </Card>

          {/* DMCA Policy */}
          <Card className="glass-card border-red-500/30 p-6">
            <h2 className="font-heading text-xl font-bold uppercase mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              DMCA & Copyright Takedown Policy
            </h2>
            <p className="text-muted-foreground mb-4">
              DMV Soundstage complies with the Digital Millennium Copyright Act (DMCA).
            </p>
            <ul className="space-y-3 text-muted-foreground mb-4">
              <li className="flex items-start gap-2">
                <span className="text-red-500 mt-1">•</span>
                Copyright holders may submit takedown requests through our designated reporting process.
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-500 mt-1">•</span>
                Content will be removed promptly upon valid notice.
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-500 mt-1">•</span>
                We will notify the uploading user of any takedown request.
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-500 mt-1">•</span>
                Repeat infringers will have their accounts terminated.
              </li>
            </ul>
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mt-4">
              <p className="text-sm font-medium mb-2">DMCA Takedown Contact:</p>
              <a href={`mailto:${dmcaEmail}`} className="text-red-400 hover:text-red-300 font-mono">
                {dmcaEmail}
              </a>
            </div>
            <p className="text-muted-foreground mt-4">
              To submit a DMCA takedown notice, please include:
            </p>
            <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
              <li>• Identification of the copyrighted work</li>
              <li>• URL of the infringing content</li>
              <li>• Your contact information</li>
              <li>• A statement of good faith belief</li>
              <li>• Your physical or electronic signature</li>
            </ul>
          </Card>

          {/* Repeat Infringer Policy */}
          <Card className="glass-card border-border p-6">
            <h2 className="font-heading text-xl font-bold uppercase mb-4">Repeat Infringer Policy</h2>
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                <span className="text-yellow-500 font-bold text-lg">1st</span>
                <div>
                  <p className="font-medium">First Violation</p>
                  <p className="text-sm text-muted-foreground">Warning and content removal</p>
                </div>
              </div>
              <div className="flex items-center gap-4 p-3 rounded-lg bg-orange-500/10 border border-orange-500/30">
                <span className="text-orange-500 font-bold text-lg">2nd</span>
                <div>
                  <p className="font-medium">Second Violation</p>
                  <p className="text-sm text-muted-foreground">Account suspension (7 days)</p>
                </div>
              </div>
              <div className="flex items-center gap-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                <span className="text-red-500 font-bold text-lg">3rd</span>
                <div>
                  <p className="font-medium">Third Violation</p>
                  <p className="text-sm text-muted-foreground">Permanent account and email ban</p>
                </div>
              </div>
            </div>
          </Card>

          {/* Platform Liability */}
          <Card className="glass-card border-border p-6">
            <h2 className="font-heading text-xl font-bold uppercase mb-4">Platform Liability Limitation</h2>
            <ul className="space-y-3 text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                DMV Soundstage is not responsible for disputes between users regarding ownership, rights, or licensing.
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                Users agree to indemnify and hold DMV Soundstage harmless from any claims arising from their uploads.
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                The Platform is provided "as is" without warranties of any kind.
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                We reserve the right to remove any content or terminate any account at our sole discretion.
              </li>
            </ul>
          </Card>

          {/* Contact */}
          <Card className="glass-card border-primary/30 p-6">
            <h2 className="font-heading text-xl font-bold uppercase mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5 text-primary" />
              Contact Us
            </h2>
            <p className="text-muted-foreground mb-4">
              For legal inquiries, DMCA notices, or questions about these terms, please contact:
            </p>
            <div className="bg-primary/10 border border-primary/30 rounded-lg p-4">
              <p className="text-sm text-muted-foreground mb-1">Legal & Copyright Inquiries:</p>
              <a href={`mailto:${contactEmail}`} className="text-primary hover:text-primary/80 font-mono text-lg">
                {contactEmail}
              </a>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
